import { put, takeEvery, take, fork, call, select, actionChannel, all } from 'redux-saga/effects';
import { noticeError } from '../../utils/noticeError';
import * as constants from '../constants';
import { marketFetchingStarted, saveMarkets } from '../actions/markets';
import { showEventLevelError } from '../actions/errors';
import { addPrice, addSelectionToProcessingQueue, addSelectionToStore, addSelectionIncompatibleError, removeSelectionFromProcessingQueue, removeSelectionFromStore, removePrice, processSelection, hideEventLevelError, setEditID, } from '../actions';
import { publishSelectionAddAnalytics, publishSelectionRemoveAnalytics } from '../../components/analyticsHelpers';
import config from '../../config';
import { getPrice, getMarketsData } from '../../dataProviders';
const selectionAreIncompatible = (selA, selB) => selA.category === selB.category
    && selA.type
    && selB.type
    && selA.type === selB.type
    && selA.team === selB.team
    && selA.period === selB.period;
export function* processSelectionSaga(action) {
    const selectionToAdd = action.payload;
    try {
        // @ts-ignore: Yield error
        const selectionsInSlip = yield select(state => Object.keys(state.slip.selections)
            .map(key => state.slip.selections[key]));
        const incompatibleSelections = selectionsInSlip.filter((sel) => selectionAreIncompatible(selectionToAdd, sel));
        const newSelections = selectionsInSlip
            .filter((sel) => !incompatibleSelections.some((incompatible) => incompatible.id === sel.id))
            .concat(selectionToAdd);
        if (newSelections && newSelections.length >= config.minimumSelections) {
            // @ts-ignore: Yield error
            const eventId = yield select(state => state.markets.data.id);
            // @ts-ignore: Yield error
            const response = yield call(getPrice, newSelections, eventId);
            yield put(addPrice(response));
        }
        yield put(addSelectionToStore(selectionToAdd));
        if (incompatibleSelections.length > 0) {
            yield all(incompatibleSelections.map((sel) => put(removeSelectionFromStore(sel))));
        }
        yield put(removeSelectionFromProcessingQueue(selectionToAdd));
        publishSelectionAddAnalytics(selectionToAdd);
    }
    catch (error) {
        // NewRelic monitoring
        noticeError(error, { errorType: constants.BYO_ERROR_BUILDSLIP });
        yield put(removeSelectionFromProcessingQueue(selectionToAdd));
        yield put(addSelectionIncompatibleError(error));
    }
}
export function* addSelectionSaga(action) {
    // @ts-ignore: Yield error
    const selectionsInSlip = yield select(state => Object.keys(state.slip.selections).length);
    // @ts-ignore: Yield error
    const selectionInProcess = yield select(state => state.slip.processingSelections.length);
    if (selectionsInSlip + selectionInProcess < config.maximumSelections) {
        yield put(addSelectionToProcessingQueue(action.payload));
        yield put(processSelection(action.payload));
    }
}
export function* removeSelectionSaga(action) {
    try {
        yield put(removeSelectionFromStore(action.payload));
        // @ts-ignore: Yield error
        const newSelections = yield select(state => Object.keys(state.slip.selections).filter(id => id !== action.payload.id).map(id => state.slip.selections[id]));
        if (newSelections && newSelections.length >= config.minimumSelections) {
            // @ts-ignore: Yield error
            const eventId = yield select(state => state.markets.data.id);
            // @ts-ignore: Yield error
            const response = yield call(getPrice, newSelections, eventId);
            yield put(addPrice(response));
        }
        else {
            yield put(removePrice());
        }
        publishSelectionRemoveAnalytics(action.payload);
    }
    catch (error) {
        // NewRelic monitoring
        noticeError(error, { errorType: constants.BYO_ERROR_BUILDSLIP });
        yield put(addSelectionIncompatibleError(error));
    }
}
export function* processSelectionWatcher() {
    // @ts-ignore: Yield error
    const channelQueue = yield actionChannel(constants.BYO_PROCESS_SELECTION);
    while (true) {
        // @ts-ignore: Yield error
        const action = yield take(channelQueue);
        yield call(processSelectionSaga, action);
    }
}
export function* editSelectionsSaga(action) {
    try {
        const { data, id, eventId } = action.payload;
        const { byoSelectionDetails } = data;
        yield put(hideEventLevelError());
        yield put(marketFetchingStarted());
        // @ts-ignore: Yield error
        const marketsResponse = yield call(getMarketsData, config.locale, eventId, true);
        const selectionsIds = new Set(byoSelectionDetails.map(({ selectionId, handicap }) => handicap ? `${selectionId}_${handicap.toFixed(2).replace('.', '-')}` : selectionId));
        const selectedSelections = [];
        marketsResponse.byoMarketGroups.forEach((marketGroup) => {
            const periods = marketGroup.periods && marketGroup.periods.length ? marketGroup.periods : [undefined];
            const teams = marketGroup.teams && marketGroup.teams.length ? marketGroup.teams : [undefined];
            periods.forEach((period) => {
                teams.forEach((team) => {
                    let selections = [];
                    if (team && period && marketGroup.markets[team][period]) {
                        ({ selections } = marketGroup.markets[team][period]);
                    }
                    if (team && !period) {
                        ({ selections } = marketGroup.markets[team]);
                    }
                    if (!team && period) {
                        ({ selections } = marketGroup.markets[period]);
                    }
                    selections.forEach((selection) => {
                        if (selectionsIds.has(selection.id)) {
                            selectedSelections.push(Object.assign({ category: marketGroup.category, categoryName: marketGroup.categoryName, team,
                                period }, selection));
                        }
                    });
                });
            });
        });
        // @ts-ignore: Yield error
        const priceResponse = yield call(getPrice, selectedSelections, marketsResponse.id);
        yield all(selectedSelections.map(selection => put(addSelectionToStore(selection))));
        yield put(addPrice(priceResponse));
        yield put(setEditID(id));
        yield put(saveMarkets(marketsResponse));
    }
    catch (error) {
        yield put(showEventLevelError(error.toString()));
    }
}
export function* editSelectionsWatcher() {
    yield takeEvery(constants.BYO_EDIT_SELECTIONS, editSelectionsSaga);
}
export function* removeSelectionWatcher() {
    yield takeEvery(constants.BYO_REMOVE_SELECTION, removeSelectionSaga);
}
export function* addSelectionWatcher() {
    yield takeEvery(constants.BYO_ADD_SELECTION, addSelectionSaga);
}
const slipSagas = [
    fork(addSelectionWatcher),
    fork(removeSelectionWatcher),
    fork(processSelectionWatcher),
    fork(editSelectionsWatcher),
];
export default slipSagas;
